// ── #3605 PR-1 — the one definition of BluePrint's estimate units (D5) ──────
//
// Points are the only stored estimate (D2, M1). The EU size is derived from
// points for display; the project's estimation type controls display only.
// Every band, mapping and display string lives here; the API and the web
// import it, never retype it (rulings on #3605, summary 6283993802).

export type EstimationType = 'STORY_POINTS' | 'EU_RU' | 'BOTH';
export type EuSize = 'S' | 'M' | 'L' | 'XL';
export type RuBand = 1 | 2 | 3;

export const EU_SIZES: readonly EuSize[] = ['S', 'M', 'L', 'XL'];
export const RU_BAND_VALUES: readonly RuBand[] = [1, 2, 3];

export interface EuBand { minMinutes: number; maxMinutes: number; targetMinutes: number }
export interface RuBandRange { minMinutes: number; maxMinutes: number }

/**
 * EU = Execution Unit: AI execution time for one Dev Agent run (D4, D6).
 * min/max are the display range; targetMinutes is the calibration target.
 */
export const EU_BANDS: Readonly<Record<EuSize, EuBand>> = {
  S:  { minMinutes: 0,  maxMinutes: 15,  targetMinutes: 12 },
  M:  { minMinutes: 15, maxMinutes: 30,  targetMinutes: 25 },
  L:  { minMinutes: 30, maxMinutes: 60,  targetMinutes: 50 },
  XL: { minMinutes: 60, maxMinutes: 120, targetMinutes: 90 },
};

/** RU = Review Unit: human review time (D7). */
export const RU_BANDS: Readonly<Record<RuBand, RuBandRange>> = {
  1: { minMinutes: 0,  maxMinutes: 20 },
  2: { minMinutes: 20, maxMinutes: 45 },
  3: { minMinutes: 45, maxMinutes: 90 },
};

/** D3, points → EU: the largest points value each size covers (XL is open-ended). */
export const EU_POINTS_MAX: Readonly<Record<Exclude<EuSize, 'XL'>, number>> = { S: 2, M: 5, L: 12 };

/** D3, EU → points: the representative points value of each size. */
export const EU_SIZE_POINTS: Readonly<Record<EuSize, number>> = { S: 2, M: 5, L: 8, XL: 13 };

/** The largest storable points value: the integer columns points are written to. */
export const MAX_STORED_POINTS = 2_147_483_647;

const EU_SIZE_SET: ReadonlySet<string> = new Set(EU_SIZES);

export function isEuSize(v: unknown): v is EuSize {
  return typeof v === 'string' && EU_SIZE_SET.has(v);
}

/** An integer from 1 to 3. */
export function isRuBand(n: unknown): n is RuBand {
  return typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= 3;
}

/**
 * The EU size for a points value (D3): ≤2 S · 3–5 M · 6–12 L · ≥13 XL.
 * Negative, 0, null, undefined and NaN give null.
 *
 * A non-integer is rounded with Math.round first — for the non-negative values
 * that reach it, the same half-away-from-zero rounding Postgres applies when a
 * fractional value is written to an integer column (issues.story_points,
 * backlog_items.estimate_points), so 2.5 reads as the stored 3 → M.
 */
export function pointsToEu(points: number | null | undefined): EuSize | null {
  if (typeof points !== 'number' || Number.isNaN(points)) return null;
  const p = Math.round(points);
  if (p <= 0) return null;
  if (p <= EU_POINTS_MAX.S) return 'S';
  if (p <= EU_POINTS_MAX.M) return 'M';
  if (p <= EU_POINTS_MAX.L) return 'L';
  return 'XL';
}

/** Story points for an EU size label (case and whitespace tolerated); null for anything else. */
export function euToPoints(size: unknown): number | null {
  if (typeof size !== 'string') return null;
  const s = size.trim().toUpperCase();
  return isEuSize(s) ? EU_SIZE_POINTS[s] : null;
}

const range = (min: number, max: number): string => (min === 0 ? `under ${max} min` : `about ${min}–${max} min`);

/** "EU M · about 15–30 min AI execution"; S reads "under 15 min". */
export function formatEu(size: EuSize): string {
  const b = EU_BANDS[size];
  return `EU ${size} · ${range(b.minMinutes, b.maxMinutes)} AI execution`;
}

/** "RU 2 · about 20–45 min human review"; 1 reads "under 20 min". */
export function formatRu(band: RuBand): string {
  const b = RU_BANDS[band];
  return `RU ${band} · ${range(b.minMinutes, b.maxMinutes)} human review`;
}

/** "5 pts"; 1 reads "1 pt". */
export function formatPoints(points: number): string {
  return `${points} ${points === 1 ? 'pt' : 'pts'}`;
}

/**
 * The display string for a stored points estimate under an estimation type.
 * STORY_POINTS "5 pts"; EU_RU "EU M · about 15–30 min AI execution";
 * BOTH "5 pts · EU M · about 15–30 min AI execution". Null, NaN, infinite and
 * negative points give null; a fraction shows as the whole points it is stored
 * as (2.5 → "3 pts", the value pointsToEu maps). Points with no EU size (0)
 * give null under EU_RU and the points alone under BOTH.
 */
export function formatEstimate(points: number | null | undefined, type: EstimationType): string | null {
  if (typeof points !== 'number' || !Number.isFinite(points) || points < 0) return null;
  const whole = Math.round(points);
  const eu = pointsToEu(whole);
  if (type === 'STORY_POINTS') return formatPoints(whole);
  if (type === 'EU_RU') return eu ? formatEu(eu) : null;
  return eu ? `${formatPoints(whole)} · ${formatEu(eu)}` : formatPoints(whole);
}

/** "≤2 S, 3–5 M, 6–12 L, ≥13 XL" — the D3 mapping as one line, for prompts. */
export function describePointsToEu(): string {
  const { S, M, L } = EU_POINTS_MAX;
  return `≤${S} S, ${S + 1}–${M} M, ${M + 1}–${L} L, ≥${L + 1} XL`;
}
